import { Controller } from 'react-hook-form';
import {
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  CardFooter,
  Button,
  Spinner,
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import TimelineHeader from '../../components/headers/TimelineHeader';
import Select from 'react-select';
import useCreateProperty from './hooks/useCreateProperty';
import MoneyInput from '../../components/MoneyInput';
import OwnerField from './components/AdditionalInfo/OwnerField';

interface Props {
  isUpdate: boolean;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  GTQ: 'Q',
  PAB: 'B/.',
};

const SectionTitle = ({ title, hint }: { title: string; hint?: string }) => (
  <div className="mt-3 mb-2 border-bottom pb-1">
    <h5 className="text-uppercase text-muted ls-1 mb-1">{title}</h5>
    {hint && <small style={{ color: '#525f7f' }}>{hint}</small>}
  </div>
);

const CreateProperty = ({ isUpdate = false }: Props) => {
  const headLabel = isUpdate ? 'Actualizar propiedad' : 'Crear propiedad';
  const navigate = useNavigate();

  const {
    isLoading,
    control,
    register,
    setValue,
    watch,
    handleSubmit,
    doAction,
    catalogs,
    errors,
    loadingCurrency,
    currentCurrency,
    marketplaceTypesList,
    marketplaceTypeSelected,
    setMarketplaceTypeSelected,
  } = useCreateProperty();

  const currency = watch('currency');
  const currencySymbol = currency
    ? (CURRENCY_SYMBOLS[currency] ?? currency)
    : '$';

  const priceLabel = marketplaceTypeSelected
    ? ` de ${marketplaceTypeSelected.label.toLowerCase()}`
    : '';

  const optionalSelect = (
    name: 'created_by' | 'source' | 'zone_of_interest',
    label: string,
    placeholder: string,
    options: string[] = [],
  ) => (
    <Col md="4">
      <FormGroup className="mb-3">
        <label className="form-control-label" htmlFor={name}>
          {label}
        </label>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <Input {...field} id={name} type="select" className="text-dark">
              <option value="">{placeholder}</option>
              {options.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Input>
          )}
        />
        {errors[name]?.message && (
          <h5 className="text-danger mb-0 mt-1">{errors[name]?.message}</h5>
        )}
      </FormGroup>
    </Col>
  );

  return (
    <>
      <TimelineHeader name={headLabel} parentName="Propiedades" />
      <Container className="mt--6" fluid>
        <Row>
          <Col xs="12">
            <Card>
              <Form role="form" onSubmit={handleSubmit(doAction)}>
                <CardHeader className="border-0 pb-2">
                  <h3 className="mb-0">{headLabel}</h3>
                  <small style={{ color: '#525f7f' }}>
                    Solo necesitas lo básico. El nombre, la ubicación, las
                    características y las fotos se completan después.
                  </small>
                </CardHeader>
                <CardBody className="pt-0 pb-3">
                  <SectionTitle title="Datos básicos" />
                  <Row>
                    {/* type */}
                    <Col lg="4" md="12">
                      <FormGroup className="mb-3">
                        <label className="form-control-label d-block">
                          Tipo de propiedad
                        </label>
                        {catalogs.propertyTypes?.map((item) => (
                          <div
                            key={item.id}
                            className="custom-control custom-radio custom-control-inline"
                          >
                            <input
                              {...register('type_id')}
                              className="custom-control-input"
                              type="radio"
                              value={item.id}
                              id={item.id}
                            />
                            <label
                              className="custom-control-label"
                              htmlFor={item.id}
                            >
                              {item.type}
                            </label>
                          </div>
                        ))}
                        {errors.type_id?.message && (
                          <h5 className="text-danger mb-0 mt-1">
                            {errors.type_id?.message}
                          </h5>
                        )}
                      </FormGroup>
                    </Col>
                    {/* Currency */}
                    <Col lg="4" md="6">
                      <FormGroup className="mb-3">
                        <label
                          className="form-control-label"
                          htmlFor="currency"
                        >
                          Moneda
                        </label>
                        {!loadingCurrency && (
                          <Controller
                            name="currency"
                            control={control}
                            defaultValue={currentCurrency.value || ''}
                            render={({ field: { ref } }) => (
                              <Select
                                placeholder="Selecciona una moneda"
                                options={catalogs.currencies}
                                defaultValue={currentCurrency}
                                onChange={(selected: any) => {
                                  const { value = '' } = selected;
                                  setValue('currency', value);
                                }}
                                ref={ref}
                              />
                            )}
                          />
                        )}
                        {errors.currency?.message && (
                          <h5 className="text-danger mb-0 mt-1">
                            {errors.currency?.message}
                          </h5>
                        )}
                      </FormGroup>
                    </Col>
                    {/* marketplace type: define si el precio es de venta o renta */}
                    <Col lg="4" md="6">
                      <FormGroup className="mb-3">
                        <label
                          className="form-control-label"
                          htmlFor="marketplace_type"
                        >
                          Tipo de marketplace
                        </label>
                        <Controller
                          name="marketplace_type"
                          control={control}
                          render={({ field: { ref } }) => (
                            <Select
                              placeholder="Venta o renta"
                              options={marketplaceTypesList}
                              onChange={(selected: any) => {
                                const { value = '' } = selected;
                                setValue('marketplace_type', value);
                                setMarketplaceTypeSelected(selected);
                              }}
                              ref={ref}
                            />
                          )}
                        />
                        {errors.marketplace_type?.message && (
                          <h5 className="text-danger mb-0 mt-1">
                            {errors.marketplace_type?.message}
                          </h5>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  <SectionTitle
                    title="Precio"
                    hint="Puedes dejarlo en 0 y definirlo después en la configuración de renta o venta."
                  />
                  <Row>
                    <Col lg="4" md="6">
                      <FormGroup className="mb-3">
                        <label className="form-control-label" htmlFor="price">
                          Precio{priceLabel}
                        </label>
                        <Controller
                          name="price"
                          control={control}
                          defaultValue={0}
                          render={({ field }) => (
                            <MoneyInput
                              id="price"
                              symbol={currencySymbol}
                              value={field.value}
                              onChange={field.onChange}
                              invalid={!!errors.price?.message}
                            />
                          )}
                        />
                        {errors.price?.message && (
                          <h5 className="text-danger mb-0 mt-1">
                            {errors.price?.message}
                          </h5>
                        )}
                      </FormGroup>
                    </Col>
                    <Col lg="4" md="6">
                      <FormGroup className="mb-3">
                        <label
                          className="form-control-label"
                          htmlFor="min_price"
                        >
                          Precio mínimo aceptable{priceLabel}
                        </label>
                        <Controller
                          name="min_price"
                          control={control}
                          defaultValue={0}
                          render={({ field }) => (
                            <MoneyInput
                              id="min_price"
                              symbol={currencySymbol}
                              value={field.value}
                              onChange={field.onChange}
                              invalid={!!errors.min_price?.message}
                            />
                          )}
                        />
                        {errors.min_price?.message && (
                          <h5 className="text-danger mb-0 mt-1">
                            {errors.min_price?.message}
                          </h5>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  <SectionTitle
                    title="Opcional"
                    hint="Se pueden asignar ahora o más tarde."
                  />
                  <Row>
                    {optionalSelect(
                      'created_by',
                      'BDR asignado',
                      'Selecciona BDR...',
                      catalogs.bdrs,
                    )}
                    {optionalSelect(
                      'source',
                      'Canal',
                      'Selecciona canal...',
                      catalogs.sources,
                    )}
                    {optionalSelect(
                      'zone_of_interest',
                      'Zonificación',
                      'Selecciona zona...',
                      catalogs.zones,
                    )}
                  </Row>
                  <FormGroup className="mb-2" style={{ maxWidth: 640 }}>
                    <label className="form-control-label">Propietario</label>
                    <OwnerField
                      onOwnerFound={(landlordId) =>
                        setValue('landlord_id', landlordId)
                      }
                      onOwnerCleared={() =>
                        setValue('landlord_id', undefined as unknown as string)
                      }
                    />
                    {errors.landlord_id?.message && (
                      <h5 className="text-danger mb-0 mt-1">
                        {errors.landlord_id?.message}
                      </h5>
                    )}
                  </FormGroup>
                </CardBody>
                <CardFooter className="text-right py-3">
                  <Button
                    type="button"
                    color="secondary"
                    outline
                    className="mr-2"
                    onClick={() => navigate('/properties/view')}
                  >
                    Cancelar
                  </Button>
                  <Button color="primary" disabled={isLoading}>
                    {isLoading && <Spinner size="sm" className="mr-2" />}
                    {headLabel}
                  </Button>
                </CardFooter>
              </Form>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default CreateProperty;
